import React, { Suspense, lazy } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { destinationApresConnexion } from './lib/utils';
import { Toaster } from './components/ui/toaster';
import RedemarrageServeur from './components/RedemarrageServeur';
import { ConfirmationMaintenueHote } from './components/commun/BoutonMaintenir';
import { useMesureUsage } from './lib/usage';
import { usePreferencesTheme } from './lib/theme';

// Pages
// v1.37.2.0 (demande du club "fait le decoupage des pages") : chaque page est chargee a la demande
// (React.lazy), au lieu d'un fichier unique de 2,7 Mo telecharge des l'ecran de connexion - un
// adherent ne recoit plus les statistiques, le materiel, la tresorerie... qu'il n'ouvre jamais.
// LoginPage reste chargee d'emblee : c'est le premier ecran, la differer ne ferait qu'ajouter un
// aller-retour avant qu'il s'affiche.
import LoginPage from './pages/LoginPage';

// Apres un deploiement, les fichiers de l'ancienne version n'existent plus : un onglet ouvert AVANT
// la mise a jour echouerait en ouvrant une page qu'il n'avait pas encore chargee (import en 404).
// On recharge alors la page, ce qui recupere la nouvelle version. Garde-fou : un seul rechargement
// automatique par tranche de 10 s - si l'echec persiste (serveur en panne), l'erreur remonte
// normalement au lieu de boucler.
const lazyPage = (charger) => lazy(() => charger().catch((erreur) => {
  let dernier = 0;
  try { dernier = Number(sessionStorage.getItem('cesam_reload_version')) || 0; } catch (e) { /* stockage indisponible */ }
  if (Date.now() - dernier < 10000) throw erreur;
  try { sessionStorage.setItem('cesam_reload_version', String(Date.now())); } catch (e) { /* idem */ }
  window.location.reload();
  return new Promise(() => {}); // rien a afficher : la page se recharge
}));
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'));
const DiversPage = lazyPage(() => import('./pages/DiversPage'));
const DiverDetailPage = lazyPage(() => import('./pages/DiverDetailPage'));
const FormationsPage = lazyPage(() => import('./pages/FormationsPage'));
const CursusPage = lazyPage(() => import('./pages/CursusPage'));
const UsersPage = lazyPage(() => import('./pages/UsersPage'));
const GdprRequestsPage = lazyPage(() => import('./pages/GdprRequestsPage'));
const AdminSettingsPage = lazyPage(() => import('./pages/AdminSettingsPage'));
const HelloAssoPage = lazyPage(() => import('./pages/HelloAssoPage'));
const EquipmentPage = lazyPage(() => import('./pages/EquipmentPage'));
const MyEquipmentPage = lazyPage(() => import('./pages/MyEquipmentPage'));
const EquipmentStatsPage = lazyPage(() => import('./pages/EquipmentStatsPage'));
const SubscriptionsPage = lazyPage(() => import('./pages/SubscriptionsPage'));
const ReceiptPage = lazyPage(() => import('./pages/ReceiptPage'));
const EmargementPage = lazyPage(() => import('./pages/EmargementPage'));
const StatsPage = lazyPage(() => import('./pages/StatsPage'));
const LoginStatsPage = lazyPage(() => import('./pages/LoginStatsPage'));
const ReservationsPage = lazyPage(() => import('./pages/ReservationsPage'));
const ScanPage = lazyPage(() => import('./pages/ScanPage'));
const CertificateAlertsPage = lazyPage(() => import('./pages/CertificateAlertsPage'));
const SubscriptionExpiredPage = lazyPage(() => import('./pages/SubscriptionExpiredPage'));
const ForgotPasswordPage = lazyPage(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyPage(() => import('./pages/ResetPasswordPage'));
// 1.49.0.0 : covoiturage ouvert aux personnes exterieures (page publique, sans compte).
const PublicCarpoolingPage = lazyPage(() => import('./pages/PublicCarpoolingPage'));
const CarpoolingPage = lazyPage(() => import('./pages/CarpoolingPage'));
const EventsPage = lazyPage(() => import('./pages/EventsPage'));
const EventParticipantPage = lazyPage(() => import('./pages/EventParticipantPage'));
const EventListePage = lazyPage(() => import('./pages/EventListePage'));
const DocumentationPage = lazyPage(() => import('./pages/DocumentationPage'));

const MyProfileRedirect = () => {
  const { user, previewAdherentDiverId } = useAuth();
  // 09/09/2026 (signalement club "je vois le tableau de bord" puis "la vue du profil adherent est
  // incomplete" en apercu role Adherent) : /mon-profil redirigeait vers /divers/{diver_id} pour un
  // adherent reel (fiche complete deja utilisee au quotidien - identite, medical, formations,
  // materiel...), mais faute de diver_id (cas de l'admin en apercu) rebondissait sur "/" - une
  // premiere correction avait fait passer ce cas par MyProfilePage.jsx (page separee, bien plus
  // pauvre - pas de formations/materiel/annotations), au lieu de rediriger vers la MEME fiche
  // complete qu'un adherent reel verrait. previewAdherentDiverId (Sophie Bernard, jeu de test)
  // tient donc lieu de user.diver_id ici, comme pour DiverDetailPage.jsx (isOwnProfile).
  const diverId = previewAdherentDiverId || user?.diver_id;
  if (diverId) return <Navigate to={`/divers/${diverId}`} replace />;
  return <Navigate to="/" replace />;
};

const ProtectedRoute = ({ children, allowedRoles = [], autoriser = null }) => {
  const { user, loading, token } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-cyan-50 to-slate-100">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-sky-500/30 mb-4 animate-pulse">
            <span className="text-white text-2xl font-bold">C</span>
          </div>
          <p className="text-slate-500">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!token || !user) {
    // 05/09/2026 (demande club "qrcode d'acces a la fiche de formation") : un scan a froid
    // (pas encore connecte) atterrissait ici, puis apres connexion repartait toujours sur "/" -
    // la destination scannee (ex: /formations?diver=152) etait perdue. redirect en query param
    // plutot que via l'etat de navigation React Router : le flux SSO WordPress fait un
    // window.history.replaceState({}, ...) sur cette meme page (cf. LoginPage.jsx), qui aurait
    // silencieusement efface un etat de navigation en le remplacant par un objet vide.
    const target = location.pathname + location.search;
    const redirect = target !== '/' ? `?redirect=${encodeURIComponent(target)}` : '';
    return <Navigate to={`/login${redirect}`} replace />;
  }
  
  // 1.75.0.0 : `autoriser` ouvre une page a un compte sans le role, sur un critere du compte
  // (ex. responsable de formation d'au moins un cursus).
  if (allowedRoles.length > 0 && !user.roles?.some(r => allowedRoles.includes(r)) && !(autoriser && autoriser(user))) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// 03/10/2026 (signalement club "en scannant le QR code d'un eleve sans etre connecte, je ne
// reviens pas sur la page d'evaluation") : a la fin d'une connexion, la page de connexion envoie
// vers la destination demandee (?redirect=), mais cette protection-ci, qui l'entoure, voyait
// l'utilisateur connecte au meme instant et renvoyait vers "/" - c'est elle qui gagnait (reproduit
// sur le parcours SSO complet). La page de connexion memorise donc la destination
// (memoriserDestination, lib/utils.js, valable 2 minutes) et la redirection ci-dessous la reprend.

const PublicRoute = ({ children }) => {
  const { user, loading, token } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-cyan-50 to-slate-100">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (token && user) {
    return <Navigate to={destinationApresConnexion(location.search)} replace />;
  }
  
  return children;
};

// Affiche pendant le telechargement d'une page (quelques centaines de ms au premier acces,
// instantane ensuite : le navigateur garde les fichiers en cache un an).
const PageLoading = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoading />}>
    <Routes>
      <Route path="/login" element={
        <PublicRoute><LoginPage /></PublicRoute>
      } />
      <Route path="/mot-de-passe-oublie" element={
        <PublicRoute><ForgotPasswordPage /></PublicRoute>
      } />
      {/* 10/09/2026 : pas de PublicRoute ici - un lien recu par email doit fonctionner meme si
          le navigateur a par ailleurs une session active (autre onglet, autre compte...). */}
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
      {/* 1.49.0.0 : lien externe du covoiturage d'une sortie - public, avec ou sans session. */}
      <Route path="/covoiturage-externe/:token" element={<PublicCarpoolingPage />} />
      <Route path="/" element={
        <ProtectedRoute><DashboardPage /></ProtectedRoute>
      } />
      {/* 08/09/2026 (signalement club "la vue adherents pour le role secretariat ne s'affiche
          pas correctement") : 'secretariat' manquait ici alors qu'il est autorise cote menu
          (Layout.jsx) ET cote backend (GET /divers, require_role([ADMIN, SECRETARIAT,
          ENCADRANT])) - un secretariat cliquant sur "Adherents" se faisait rediriger vers "/"
          sans explication (ProtectedRoute renvoie silencieusement a l'accueil sur role non
          autorise), ce qui ressemblait a une page cassee plutot qu'a un refus d'acces. */}
      <Route path="/divers" element={
        <ProtectedRoute allowedRoles={['admin', 'encadrant', 'secretariat']}><DiversPage /></ProtectedRoute>
      } />
      <Route path="/divers/:diverId" element={
        <ProtectedRoute><DiverDetailPage /></ProtectedRoute>
      } />
      <Route path="/certificats" element={
        <ProtectedRoute allowedRoles={['admin', 'encadrant', 'secretariat']}><CertificateAlertsPage /></ProtectedRoute>
      } />
      <Route path="/mon-profil" element={
        <ProtectedRoute allowedRoles={['adherent']}><MyProfileRedirect /></ProtectedRoute>
      } />
      <Route path="/mon-materiel" element={
        <ProtectedRoute allowedRoles={['adherent']}><MyEquipmentPage /></ProtectedRoute>
      } />
      <Route path="/formations" element={
        <ProtectedRoute><FormationsPage /></ProtectedRoute>
      } />
      <Route path="/cursus" element={
        <ProtectedRoute allowedRoles={['admin', 'responsable_technique']} autoriser={u => (u.cursus_responsable || []).length > 0}><CursusPage /></ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={['admin']}><UsersPage /></ProtectedRoute>
      } />
      {/* 22/09/2026 (RGPD phase 1) : admin uniquement, meme severite que la suppression
          definitive d'un adherent - pas ouvert au secretariat. */}
      <Route path="/gdpr-requests" element={
        <ProtectedRoute allowedRoles={['admin']}><GdprRequestsPage /></ProtectedRoute>
      } />
      <Route path="/equipment" element={
        <ProtectedRoute allowedRoles={['admin', 'gestionnaire_materiel']}><EquipmentPage /></ProtectedRoute>
      } />
      <Route path="/equipment/stats" element={
        <ProtectedRoute allowedRoles={['admin', 'gestionnaire_materiel']}><EquipmentStatsPage /></ProtectedRoute>
      } />
      <Route path="/reservations" element={
        <ProtectedRoute allowedRoles={['admin', 'encadrant', 'gestionnaire_materiel']}><ReservationsPage /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}><AdminSettingsPage /></ProtectedRoute>
      } />
      {/* 18/09/2026 (demande du club) : recu de paiement imprimable par le secretariat.
          22/09/2026 (demande du club "il doit acceder a son propre recu depuis son propre
          espace") : plus de restriction de role ici, meme schema que /divers/:diverId - la
          vraie autorisation (staff, ou proprietaire de CETTE adhesion precise) est verifiee
          cote backend (GET /subscriptions/{id}/recu), pas devinable au niveau de la route. */}
      <Route path="/subscriptions/:subId/recu" element={
        <ProtectedRoute><ReceiptPage /></ProtectedRoute>
      } />
      {/* 1.56.0.0 (demande du club) : liste d'emargement imprimable des adherents a jour. */}
      <Route path="/subscriptions/emargement" element={
        <ProtectedRoute allowedRoles={['admin', 'secretariat']}><EmargementPage /></ProtectedRoute>
      } />
      <Route path="/subscriptions" element={
        <ProtectedRoute allowedRoles={['admin', 'secretariat']}><SubscriptionsPage /></ProtectedRoute>
      } />
      <Route path="/stats" element={
        <ProtectedRoute allowedRoles={['admin', 'secretariat']}><StatsPage /></ProtectedRoute>
      } />
      <Route path="/connexions" element={
        <ProtectedRoute allowedRoles={['admin']}><LoginStatsPage /></ProtectedRoute>
      } />
      <Route path="/tresorerie" element={
        <ProtectedRoute allowedRoles={['admin', 'tresorier']}><HelloAssoPage /></ProtectedRoute>
      } />
      <Route path="/evenements" element={
        <ProtectedRoute><EventsPage /></ProtectedRoute>
      } />
      {/* 25/09/2026 : pas de allowedRoles ici, meme patron que /divers/:diverId - la protection
          reelle est cote serveur (_appliquer_visibilite_participants), un role non autorise
          tombe sur l'etat "introuvable" de la page elle-meme. */}
      {/* 1.51.0.0 : liste des participants avec verifications - controle d'acces cote serveur. */}
      <Route path="/evenements/:eventId/liste" element={
        <ProtectedRoute><EventListePage /></ProtectedRoute>
      } />
      <Route path="/evenements/:eventId/participants/:index" element={
        <ProtectedRoute><EventParticipantPage /></ProtectedRoute>
      } />
      {/* 1.27.0.0 : ouverte a tout compte connecte - c'est le serveur qui decide quels manuels
          apparaissent, selon les roles. Un adherent y arrive sur une page vide, pas sur un refus. */}
      <Route path="/documentation" element={
        <ProtectedRoute><DocumentationPage /></ProtectedRoute>
      } />
      <Route path="/covoiturage" element={
        <ProtectedRoute><CarpoolingPage /></ProtectedRoute>
      } />
      <Route path="/scan/:equipmentId" element={<ScanPage />} />
      <Route path="/adhesion-expiree" element={<SubscriptionExpiredPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

// 1.47.0.0 (demande du club) : mesure d'usage anonyme, par role (lib/usage.js). Une seule fois pour
// toute l'application - dans Layout, elle repartirait de zero a chaque page. Coupee pendant un
// apercu de role ou de donnees de test (demonstrations).
const MesureUsage = () => {
  const { user, token, isPreviewingRole, isPreviewingAppMode } = useAuth();
  const { theme } = usePreferencesTheme();
  useMesureUsage({ actif: !!(token && user) && !isPreviewingRole && !isPreviewingAppMode, theme, diverId: user?.diver_id });
  return null;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <MesureUsage />
        <AppRoutes />
        <Toaster />
        <RedemarrageServeur />
        <ConfirmationMaintenueHote />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
